/**
 * AppApertureBar — the bottom-Aperture chrome for the web app.
 *
 * Alternative to AppSidebar.  Rendered by RootLayout when the
 * studio's `useNavStyle()` preference is `'aperture'` (chosen in
 * the welcome sequence or Settings → Navigation).
 *
 * Same visual DNA as the /aperture-demo BottomLayout + iOS
 * RootTabView.swift's tab bar:
 *   • Solid translucent pill with subtle border
 *   • Framer Motion `layoutId` matched-geometry pill morph
 *   • Colored bloom beneath/behind the pill in the current
 *     tab-accent, breathing slowly, colour cross-fades via the
 *     @property-registered --orbit-tab-glow-primary
 *   • Text-only tabs, active label picks up the tab-accent
 *   • Horizontal scroll with edge-fade masks + auto-center on
 *     active tab
 *
 * POL-17 — glow + glitch pass:
 *   • FIX: the dock-lift arc used to render inside the
 *     `overflow-x-auto` scroller with `overflowY: visible` — but
 *     per the CSS spec overflow-y computes to `auto` when the other
 *     axis is non-visible, so lifted tabs CLIPPED against the pill
 *     top and stuttered (the "glitchy" bar).  The scroller now owns
 *     LIFT_HEADROOM of transparent headroom above the pill surface,
 *     so the arc plays out inside the scroll container and nothing
 *     clips.  The visual pill is an absolutely-positioned backdrop
 *     that only covers the bottom band.
 *   • Glow: three layers (wide bloom / mid halo / hot underline) at
 *     much higher opacity + reach, with a slow breathing loop.
 *     Reduced-motion users get the static glow, no pulse.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { cn } from '@/lib/utils';
import { useSimplifiedNav } from '@/hooks/useSimplifiedNav';
// MC-2 — Admin tab on the bar, ONLY for accounts with an active
// admin_users row (same gate as AdminRoute / the sidebar Admin link).
import { useAdminAuth } from '@/hooks/useAdmin';
// CHROME-1 — the six-destination structure now lives in one place
// (`@/lib/navDestinations`) and the studio's chosen subset + order
// comes from one hook, shared with AppSidebar.  Two chromes, one
// list — see the header comment in navDestinations.ts.
import {
  NAV_DESTINATIONS,
  sectionAccentForNavId,
  type NavDestination,
} from '@/lib/navDestinations';
import { useNavItems } from '@/hooks/useNavItems';

/**
 * BRAND-2 — per-destination chrome accent.  The pill is dark glass
 * in BOTH themes, so tabs use the theme-INDEPENDENT
 * `--orbit-chrome-*` tones (see index.css), keyed off the one
 * route → accent registry in navDestinations.  Used for the hover
 * pre-show (a tab's identity shows under the cursor BEFORE you
 * commit) and for embedded marketing mounts, which can't read the
 * live `html[data-orbit-tab]` variables.
 */
function chromeAccent(id: string): string {
  return `--orbit-chrome-${sectionAccentForNavId(id)}`;
}

type Destination = NavDestination;

// Full Aperture destination list — the CLASSIC (non-simplified)
// fourteen.  Keep this in sync with `AppSidebar.navItems` so the two
// chromes never drift again.  Any new sidebar entry MUST be added
// here too.  The simplified six now live in `@/lib/navDestinations`
// and are shared with the rail outright, which is where new work
// should go — this list is the legacy path behind a Settings toggle.
const DESTINATIONS: Destination[] = [
  { id: 'today',       label: 'Today',       href: '/' },
  // Nova Design is now a mode inside Nova (/assistant?mode=design),
  // so the old 'nova-design' destination is gone — one Nova entry.
  { id: 'chat',        label: 'Nova',        href: '/assistant' },
  { id: 'passport',    label: 'Passport',    href: '/galaxy' },
  { id: 'projects',    label: 'Projects',    href: '/projects' },
  { id: 'clients',     label: 'Clients',     href: '/clients' },
  { id: 'inquiries',   label: 'Inquiries',   href: '/inquiries' },
  { id: 'calendar',    label: 'Calendar',    href: '/calendar' },
  { id: 'expenses',    label: 'Expenses',    href: '/expenses' },
  { id: 'galleries',   label: 'Galleries',   href: '/galleries' },
  { id: 'sites',       label: 'Sites',       href: '/sites' },
  { id: 'emails',      label: 'Emails',      href: '/emails' },
  { id: 'booking',     label: 'Booking',     href: '/settings?tab=scheduling' },
  { id: 'settings',    label: 'Settings',    href: '/settings' },
];

// NAV-6 wave (a) — the approved six-tab simplified structure.
// Routes/pages don't change; the nav just files them.  Passport
// leaves the bar (reachable from Today's rings/goals band) and
// Settings becomes the gear pinned to the pill's right.
//   UX-1 — destination 5 (id `business`) is labelled "Galleries"
//   now; see navDestinations.ts.  NEVER "Studio".
// CHROME-1 — the list itself moved to `@/lib/navDestinations` so the
// rail reads the same one.  Kept as a named alias because the
// matchers below (and a couple of tests) refer to it by this name.
const SIMPLIFIED_DESTINATIONS: Destination[] = NAV_DESTINATIONS;

/** NAV-6 — does `href` match the current location?  Base path is a
 *  prefix match ('/' must be exact); an `?tab=` query in the href
 *  must also appear in the live search string (so Booking's
 *  `/settings?tab=scheduling` only matches the scheduling tab, and
 *  plain `/settings` stays with the gear). */
function hrefMatches(href: string, pathname: string, search: string): boolean {
  const [base, query] = href.split('?');
  const pathOk = base === '/' ? pathname === '/' : pathname.startsWith(base);
  if (!pathOk) return false;
  if (query && !search.includes(query)) return false;
  return true;
}

/** NAV-6 — simplified active match.  A destination is active when
 *  the location matches its primary href OR ANY of its sub-routes
 *  (/inquiries lights Clients, /sites lights Business, etc.).
 *  Returns null on unfiled routes (e.g. plain /settings — the gear
 *  owns that) so no tab lights up dishonestly.
 *
 *  CHROME-1 — matches against the destinations actually ON the bar,
 *  not the full catalogue.  If a studio has hidden Business, landing
 *  on /galleries lights nothing rather than lighting a tab that
 *  isn't rendered (which would orphan the layoutId pill). */
function simplifiedActiveIdFor(
  destinations: Destination[],
  pathname: string,
  search: string,
): string | null {
  for (const d of destinations) {
    const hrefs = [d.href, ...(d.subs?.map((s) => s.href) ?? [])];
    if (hrefs.some((h) => hrefMatches(h, pathname, search))) return d.id;
  }
  return null;
}

// Headroom above the pill that the dock-lift arc can rise into
// without clipping.  Must be >= MAX_LIFT + a couple px of shadow.
const LIFT_HEADROOM = 20;
const MAX_LIFT = 14;
/** NAV-7 — breathing room between the pill's inner edge and the tabs,
 *  top and bottom. It exists as a constant because the tab's top
 *  margin and the list's bottom padding have to stay equal or the
 *  labels sit off-centre — which is exactly the bug this fixes. */
const PILL_PAD = 7;

// Longest-prefix match — walks in reverse so more specific entries
// (`/settings?tab=scheduling`) win over shorter prefixes
// (`/settings`, `/`).
function activeIdFor(pathname: string, search = ''): string {
  // AUDIT-1 — Booking and Settings share the /settings base; a
  // destination whose href carries a query only wins when that query
  // is live, so /settings?tab=billing lights Settings, not Booking.
  const sorted = [...DESTINATIONS].sort(
    (a, b) => b.href.split('?')[0].length - a.href.split('?')[0].length,
  );
  for (const d of sorted) {
    const [base, query] = d.href.split('?');
    if (query && !search.includes(query)) continue;
    if (base === '/' ? pathname === '/' : pathname.startsWith(base)) {
      return d.id;
    }
  }
  return 'today';
}

export interface AppApertureBarProps {
  /**
   * MKTG-3 — private path for embedded, non-navigating mounts.
   *
   * The public marketing page mounts this exact bar four times so
   * visitors can play with the real chrome before signing up. It
   * used to hand each mount its own history via `<MemoryRouter>`,
   * but react-router v6 hard-throws
   * ("You cannot render a <Router> inside another <Router>") when a
   * Router is nested inside the app's BrowserRouter — and in the
   * production bundle that invariant is stripped to a *messageless*
   * Error, so the page died behind a blank error card.
   *
   * So the bar takes the private path as a prop instead of a private
   * router. Accepts an optional query (`/settings?tab=scheduling`),
   * matching the `href` shape in DESTINATIONS. When omitted (the
   * normal in-app case) the live router location is used, unchanged.
   */
  path?: string;
  /**
   * Navigation override for embedded mounts. Defaults to the live
   * router's `navigate`, so RootLayout keeps its existing behaviour.
   */
  onNavigate?: (href: string) => void;
}

export function AppApertureBar({ path, onNavigate }: AppApertureBarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [simplified] = useSimplifiedNav();
  // CHROME-1 — the studio's chosen destinations + order.  Shared
  // with AppSidebar so the two chromes can't disagree.
  const { destinations: chosen } = useNavItems();

  // Private path (embedded mount) or the live location.
  const [overridePathname, overrideSearch] = path
    ? [path.split('?')[0], path.includes('?') ? `?${path.split('?')[1]}` : '']
    : [null, null];
  const pathname = overridePathname ?? location.pathname;
  const search = overrideSearch ?? location.search;
  const go = onNavigate ?? navigate;

  // MC-2 — Mission Control entry for admin accounts only. Appended
  // to BOTH nav modes; non-admins (and the loading state) never see
  // it. Note the bar itself unmounts inside /admin (AdminLayout owns
  // that chrome), so this tab is a launcher — it never needs an
  // active state here.
  const { isAdmin } = useAdminAuth();

  // NAV-6 — pick the destination set + matcher per mode.  Classic
  // keeps the longest-prefix walk untouched; simplified matches
  // against primary + sub-routes and may return null (gear routes).
  //
  // CHROME-1 — embedded mounts (the public marketing page passes
  // `path`) always render the canonical, unedited bar.  A visitor
  // should see what Orbit ships, not a preference some other tab
  // wrote into this browser's localStorage.
  const embedded = path !== undefined;
  const simplifiedSet: Destination[] = embedded
    ? SIMPLIFIED_DESTINATIONS
    : chosen;
  const baseDestinations = simplified ? simplifiedSet : DESTINATIONS;
  const destinations: Destination[] = isAdmin
    ? [...baseDestinations, { id: 'admin', label: 'Admin', href: '/admin' }]
    : baseDestinations;
  const activeId: string | null = simplified
    ? simplifiedActiveIdFor(simplifiedSet, pathname, search)
    : activeIdFor(pathname, search);

  // The active destination's sub-row (simplified only).
  const activeDest = simplified
    ? simplifiedSet.find((d) => d.id === activeId)
    : undefined;
  const activeSubId =
    activeDest?.subs?.find((s) => hrefMatches(s.href, pathname, search))?.id ??
    null;
  const gearActive =
    simplified && pathname.startsWith('/settings') && activeId == null;

  // Auto-center the active tab in the scrollable rail.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // #494 rev — macOS dock-style magnification.  When a cursor
  // hovers the bar we track its X position and lift each tab
  // based on distance from the cursor (cosine falloff).  Touch
  // devices don't fire mouse events, so on mobile web the bar
  // renders flat — as intended.
  const [cursorX, setCursorX] = useState<number | null>(null);
  const [tabCenters, setTabCenters] = useState<Record<string, number>>({});

  // Recompute tab centers on scroll + resize so magnification
  // tracks the current on-screen positions, not the initial ones.
  useEffect(() => {
    function recompute() {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const scrollerBox = scroller.getBoundingClientRect();
      const centers: Record<string, number> = {};
      for (const [id, el] of Object.entries(itemRefs.current)) {
        if (!el) continue;
        const box = el.getBoundingClientRect();
        centers[id] = box.left + box.width / 2 - scrollerBox.left;
      }
      setTabCenters(centers);
    }
    recompute();
    const scroller = scrollerRef.current;
    scroller?.addEventListener('scroll', recompute);
    window.addEventListener('resize', recompute);
    return () => {
      scroller?.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
    // NAV-6 — re-measure when the destination set swaps live
    // (classic ↔ simplified from Settings) so magnification tracks
    // the new tab layout without waiting for a scroll/resize.
    // CHROME-1 — same for the studio editing which destinations are
    // on the bar; the tab centres move the moment a toggle flips.
  }, [simplified, chosen]);

  /** #494 rev v3 — Vertical lift only (no text scaling per
   *  Rennan's callout).  The row of tabs arcs upward toward
   *  the cursor like a rainbow curve.  Cosine falloff for a
   *  smooth swell. */
  function magnificationLift(tabCenter: number): number {
    if (cursorX == null) return 0;
    const MAX_INFLUENCE = 130;
    const distance = Math.abs(tabCenter - cursorX);
    if (distance > MAX_INFLUENCE) return 0;
    const t = distance / MAX_INFLUENCE;
    return MAX_LIFT * Math.cos((t * Math.PI) / 2);
  }

  /** The tab whose center is currently closest to the cursor. */
  const hoveredId: string | null = (() => {
    if (cursorX == null) return null;
    let bestId: string | null = null;
    let bestDist = Infinity;
    for (const [id, center] of Object.entries(tabCenters)) {
      const d = Math.abs(center - cursorX);
      if (d < bestDist) {
        bestDist = d;
        bestId = id;
      }
    }
    return bestId;
  })();

  /** #494 rev v2 — Edge-scroll.  When the cursor sits within
   *  EDGE_TRIGGER px of either end of the bar, auto-scroll toward
   *  that edge; speed grows with depth into the zone. */
  useEffect(() => {
    if (cursorX == null) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const barWidth = scroller.clientWidth;
    const EDGE_TRIGGER = 60;
    const EDGE_MAX_SPEED = 8; // px per frame

    let delta = 0;
    if (cursorX < EDGE_TRIGGER) {
      const depth = (EDGE_TRIGGER - cursorX) / EDGE_TRIGGER;
      delta = -EDGE_MAX_SPEED * depth;
    } else if (cursorX > barWidth - EDGE_TRIGGER) {
      const depth = (cursorX - (barWidth - EDGE_TRIGGER)) / EDGE_TRIGGER;
      delta = EDGE_MAX_SPEED * depth;
    }
    if (delta === 0) return;

    let raf = 0;
    const tick = () => {
      scroller.scrollLeft += delta;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cursorX]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const el = activeId ? itemRefs.current[activeId] : null;
    if (!scroller || !el) return;
    scroller.scrollTo({
      left: el.offsetLeft - scroller.clientWidth / 2 + el.clientWidth / 2,
      behavior: 'smooth',
    });
  }, [activeId]);

  // BRAND-2 — embedded mounts (marketing page) have no live
  // `html[data-orbit-tab]` to inherit accents from — and four
  // sibling mounts couldn't share one <html> attribute anyway.  So
  // an embedded bar carries its OWN accent variables, resolved from
  // the active destination (preferring the active sub-route: the
  // Business mount on /sites should read teal, not galleries'
  // silver).  Live in-app mounts leave this undefined and keep the
  // html-driven variables, including the @property glow tween.
  const embeddedAccentId = activeSubId ?? activeId;
  const embeddedAccentStyle =
    embedded && embeddedAccentId
      ? ({
          '--orbit-tab-accent': `var(${chromeAccent(embeddedAccentId)})`,
          '--orbit-tab-glow-primary': `hsl(var(${chromeAccent(embeddedAccentId)}) / 0.34)`,
          '--orbit-tab-glow-accent': `hsl(var(${chromeAccent(embeddedAccentId)}) / 0.18)`,
        } as CSSProperties)
      : undefined;

  return (
    <nav
      // FIX-5 (AUDIT-2) — `pointer-events-none` on the shell: the
      // container below carries LIFT_HEADROOM of transparent padding
      // for the dock-lift arc, and that invisible band was eating
      // clicks meant for page content just above the pill (e.g. the
      // "Log your first expense" empty-state button). The shell now
      // lets clicks fall through; every interactive child (sub-pill
      // row, gear, scroller) re-enables with `pointer-events-auto`.
      className="pointer-events-none fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center"
      aria-label="Aperture Bar"
      style={embeddedAccentStyle}
    >
      {/* POL-17 breathing keyframes — scoped to this nav. */}
      <style>
        {`
          @keyframes apertureBloomBreathe {
            0%, 100% { opacity: 0.95; transform: scale(1.9, 3.0); }
            50%      { opacity: 0.7;  transform: scale(1.7, 2.6); }
          }
          @keyframes apertureCoreBreathe {
            0%, 100% { opacity: 0.95; }
            50%      { opacity: 0.7; }
          }
          @media (prefers-reduced-motion: reduce) {
            .aperture-bloom, .aperture-core { animation: none !important; }
          }
        `}
      </style>

      {/* NAV-6 — sub-pill row.  When the active simplified
          destination files sub-routes, a smaller glass capsule
          floats above the main pill so Projects/Calendar/Booking
          etc. stay one tap away.  Negative margin tucks it 6px
          above the pill surface (the container's top LIFT_HEADROOM
          is transparent arc headroom, not visual pill). */}
      {simplified && activeDest?.subs && (
        <div
          className="pointer-events-auto relative flex items-center gap-1 px-1.5 py-1 rounded-full whitespace-nowrap"
          style={{
            marginBottom: -(LIFT_HEADROOM - 6),
            zIndex: 3,
            background:
              'linear-gradient(90deg, rgba(10, 11, 16, 0.92) 0%, rgba(22, 24, 32, 0.88) 50%, rgba(10, 11, 16, 0.92) 100%)',
            border: '1px solid rgb(255 255 255 / 0.14)',
            boxShadow:
              '0 4px 16px rgba(0,0,0,0.5), 0 0 0 1px rgb(255 255 255 / 0.03), inset 0 1px 0 rgb(255 255 255 / 0.07)',
          }}
        >
          {activeDest.subs.map((s) => {
            const subActive = s.id === activeSubId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => go(s.href)}
                className="relative px-3 py-0.5 rounded-full transition-colors duration-300"
                style={{
                  color: subActive
                    ? 'hsl(var(--orbit-tab-accent))'
                    : 'rgb(255 255 255 / 0.55)',
                }}
              >
                {subActive && (
                  <motion.span
                    layoutId="app-aperture-subpill"
                    className="absolute inset-0 rounded-full"
                    style={{
                      backgroundColor: 'rgb(18 20 24 / 0.96)',
                      boxShadow:
                        'inset 0 1px 0 rgb(255 255 255 / 0.05), 0 0 12px hsl(var(--orbit-tab-accent) / 0.35)',
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 420,
                      damping: 36,
                      mass: 0.9,
                    }}
                  />
                )}
                <span className="relative z-10 font-semibold text-[12px] tracking-tight">
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* The pill's visual box — everything (glow, backdrop, edge
          fades) anchors to THIS, not the lift headroom above it.
          NAV-6 — six tabs need no horizontal scroll, so simplified
          drops the fixed width and lets the pill hug its content. */}
      <div
        className="relative"
        // NAV-8 — `data-aperture-dock` and the headroom variable exist
        // so a design can hang decoration off the PILL rather than off
        // the nav. The nav's box includes the transparent lift
        // headroom (and, in simplified mode, the sub-pill row above),
        // so anything sized against it floats above the bar instead of
        // hugging it — which is exactly what the redesign's aura was
        // doing. Pill height is `100% - var(--aperture-headroom)` of
        // this element, and stays right if the padding ever changes.
        data-aperture-dock
        style={{
          width: simplified ? 'fit-content' : 'min(720px, calc(100vw - 48px))',
          maxWidth: simplified ? 'min(560px, calc(100vw - 48px))' : undefined,
          paddingTop: LIFT_HEADROOM,
          ['--aperture-headroom' as string]: `${LIFT_HEADROOM}px`,
        }}
      >
        {/* ── POL-17 glow stack ──────────────────────────────────
            Three layers, all driven by --orbit-tab-glow-primary so
            the whole halo cross-fades on tab change:
              1. Wide bloom — huge soft spill, slow breathing.
              2. Mid halo — tighter, hotter ring around the pill.
              3. Hot underline — a thin bright ellipse hugging the
                 pill's bottom edge, like light leaking under it. */}
        <div
          aria-hidden
          className="aperture-bloom absolute rounded-full pointer-events-none"
          style={{
            inset: `${LIFT_HEADROOM}px 0 0 0`,
            background:
              'radial-gradient(ellipse 75% 110% at center, var(--orbit-tab-glow-primary) 0%, transparent 70%)',
            filter: 'blur(56px)',
            opacity: 0.95,
            transform: 'scale(1.9, 3.0)',
            animation: 'apertureBloomBreathe 5.5s ease-in-out infinite',
            transition: 'background 300ms ease',
            zIndex: -1,
          }}
        />
        <div
          aria-hidden
          className="aperture-core absolute rounded-full pointer-events-none"
          style={{
            inset: `${LIFT_HEADROOM}px 0 0 0`,
            background: 'var(--orbit-tab-glow-primary)',
            filter: 'blur(24px)',
            opacity: 0.9,
            transform: 'scale(1.25, 1.55)',
            animation: 'apertureCoreBreathe 5.5s ease-in-out infinite',
            transition: 'background 300ms ease',
            zIndex: -1,
          }}
        />
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            left: '8%',
            right: '8%',
            bottom: -6,
            height: 14,
            borderRadius: 9999,
            background: 'var(--orbit-tab-glow-primary)',
            filter: 'blur(10px)',
            opacity: 0.85,
            transition: 'background 300ms ease',
            zIndex: -1,
          }}
        />

        {/* Pill backdrop — bottom band only; the headroom above
            stays transparent so the lift arc floats over the page. */}
        <div
          aria-hidden
          className="absolute left-0 right-0 bottom-0 rounded-full"
          style={{
            top: LIFT_HEADROOM,
            background:
              'linear-gradient(90deg, rgba(10, 11, 16, 0.92) 0%, rgba(22, 24, 32, 0.88) 50%, rgba(10, 11, 16, 0.92) 100%)',
            border: '1px solid rgb(255 255 255 / 0.16)',
            boxShadow:
              '0 6px 22px rgba(0,0,0,0.55), 0 22px 52px rgba(0,0,0,0.42), 0 0 0 1px rgb(255 255 255 / 0.04), inset 0 1px 0 rgb(255 255 255 / 0.08), inset 0 -10px 26px rgba(0, 0, 0, 0.42)',
          }}
        />

        {/* Edge fade masks — pinned to the pill band.  NAV-6 —
            skipped in simplified mode: nothing scrolls, so fading
            the first/last tab would just dim Today and Money. */}
        {!simplified && (
          <>
            <div
              aria-hidden
              className="absolute left-0 bottom-0 w-12 pointer-events-none rounded-l-full"
              style={{
                top: LIFT_HEADROOM,
                background:
                  'linear-gradient(to right, rgba(10, 11, 16, 1) 0%, rgba(10, 11, 16, 0) 100%)',
                zIndex: 2,
              }}
            />
            <div
              aria-hidden
              className="absolute right-0 bottom-0 w-12 pointer-events-none rounded-r-full"
              style={{
                top: LIFT_HEADROOM,
                background:
                  'linear-gradient(to left, rgba(10, 11, 16, 1) 0%, rgba(10, 11, 16, 0) 100%)',
                zIndex: 2,
              }}
            />
          </>
        )}

        {/* NAV-6 — Settings leaves the bar in simplified mode and
            becomes a small gear pinned to the pill's right, same
            glass language.  Lights the accent when /settings is
            open (outside the Booking sub-tab, which Work owns). */}
        {simplified && (
          <button
            type="button"
            aria-label="Settings"
            onClick={() => go('/settings')}
            className="pointer-events-auto absolute flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-300"
            style={{
              left: 'calc(100% + 10px)',
              top: LIFT_HEADROOM,
              bottom: 0,
              margin: 'auto 0',
              background:
                'linear-gradient(90deg, rgba(10, 11, 16, 0.92) 0%, rgba(22, 24, 32, 0.88) 100%)',
              border: '1px solid rgb(255 255 255 / 0.16)',
              boxShadow:
                '0 6px 22px rgba(0,0,0,0.55), 0 0 0 1px rgb(255 255 255 / 0.04), inset 0 1px 0 rgb(255 255 255 / 0.08)',
              color: gearActive
                ? 'hsl(var(--orbit-tab-accent))'
                : 'rgb(255 255 255 / 0.6)',
            }}
          >
            <OrbitIcon name="gearshape" fallback={Settings} className="w-4 h-4" />
          </button>
        )}

        {/* Scroller — includes the lift headroom INSIDE its bounds,
            so the arc never clips (the old overflowY:'visible'
            never actually worked: overflow-y computes to auto when
            overflow-x is auto, which clipped + stuttered). */}
        <div
          ref={scrollerRef}
          className="pointer-events-auto relative flex-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden"
          // NAV-7 — `overflow-x: auto` computes overflow-y to auto
          // too, so a tab lifting out of this box would clip. The
          // padding gives the arc somewhere to go and the equal
          // negative margin puts the box back where it was, so the
          // tabs don't move a pixel.
          style={{
            scrollbarWidth: 'none',
            zIndex: 1,
            paddingTop: MAX_LIFT,
            marginTop: -MAX_LIFT,
          }}
          onMouseMove={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            setCursorX(e.clientX - box.left);
          }}
          onMouseLeave={() => setCursorX(null)}
        >
          <ul className="flex items-center gap-1 px-1.5 whitespace-nowrap" style={{ paddingBottom: PILL_PAD }}>
            {destinations.map((d) => {
              const active = d.id === activeId;
              const hovered = d.id === hoveredId;
              const lift = magnificationLift(tabCenters[d.id] ?? -Infinity);
              // BRAND-2 — the ACTIVE tab keeps the route-driven
              // `--orbit-tab-accent` (it tweens, and it follows the
              // active SUB's accent — Business reads teal on /sites,
              // silver on /galleries).  A HOVERED tab pre-shows its
              // OWN destination accent instead of echoing the
              // current one — the identity shows before you commit.
              const color = active
                ? 'hsl(var(--orbit-tab-accent))'
                : hovered
                  ? `hsl(var(${chromeAccent(d.id)}))`
                  : 'rgb(255 255 255 / 0.55)';
              return (
                <li key={d.id}>
                  <button
                    ref={(el) => {
                      itemRefs.current[d.id] = el;
                    }}
                    type="button"
                    onClick={() => go(d.href)}
                    className={cn(
                      'relative px-3.5 py-1 rounded-full transition-colors duration-300',
                    )}
                    style={{
                      color,
                      // NAV-7 — this used to be `marginTop:
                      // LIFT_HEADROOM`, which double-counted the arc
                      // headroom: the pill's box already reserves it
                      // as padding-top, so the extra 20px pushed
                      // every label 20px below the pill's centre.
                      // Rennan: "the buttons are near the bottom of
                      // the bar which looks off." They were. The room
                      // for the lift arc now lives in the scroller's
                      // own padding (see below), where it belongs,
                      // and this is just the pill's inner padding.
                      marginTop: PILL_PAD,
                      transform: `translateY(-${lift}px)`,
                      transition:
                        'transform 140ms cubic-bezier(0.22, 0.61, 0.36, 1), color 300ms',
                    }}
                  >
                    {active && (
                      <>
                        <motion.div
                          layoutId="app-aperture-bubble"
                          className="absolute inset-0 rounded-full pointer-events-none"
                          initial={{ opacity: 1 }}
                          animate={{ opacity: 0.7 }}
                          transition={{
                            layout: { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 },
                            opacity: { duration: 0.9, ease: 'easeOut' },
                          }}
                          style={{
                            background: 'var(--orbit-tab-glow-primary)',
                            filter: 'blur(7px)',
                            transform: 'scale(1.2)',
                            mixBlendMode: 'plus-lighter',
                          }}
                        />
                        <motion.div
                          layoutId="app-aperture-pill"
                          className="absolute inset-0 rounded-full"
                          style={{
                            backgroundColor: 'rgb(18 20 24 / 0.96)',
                            boxShadow:
                              'inset 0 1px 0 rgb(255 255 255 / 0.04), inset 0 -1px 0 rgb(0 0 0 / 0.35)',
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 420,
                            damping: 36,
                            mass: 0.9,
                          }}
                        />
                      </>
                    )}
                    {/* POL-17 — hovered (non-active) tabs get a faint
                        accent bubble so the identity pre-shows under
                        the cursor. Static (no layoutId) so it never
                        fights the active pill morph.
                        BRAND-2 — the bubble is the HOVERED tab's own
                        destination accent now, not the current
                        route's glow: hover Money, see phosphor. */}
                    {hovered && !active && (
                      <span
                        aria-hidden
                        className="absolute inset-0 rounded-full pointer-events-none"
                        style={{
                          background: `hsl(var(${chromeAccent(d.id)}) / 0.34)`,
                          opacity: 0.28,
                          filter: 'blur(8px)',
                          mixBlendMode: 'plus-lighter',
                        }}
                      />
                    )}
                    <span className="relative z-10 font-semibold text-[13px] tracking-tight">
                      {d.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default AppApertureBar;
